import type { ProcessingType, SearchType, SavedPaymentMethod } from '@/types';
import { logger, ValidationError, NetworkError, ProcessingError } from '@/utils/logger';

const RAW_STRIPE_API_URL = import.meta.env.VITE_STRIPE_API_URL;
const STRIPE_API_URL =
  typeof RAW_STRIPE_API_URL === 'string' && RAW_STRIPE_API_URL.trim().length > 0
    ? RAW_STRIPE_API_URL.trim()
    : null;

if (!STRIPE_API_URL && import.meta.env.DEV) {
  console.warn(
    '[StripeAPI] VITE_STRIPE_API_URL is not set. Payment-related API calls will fail in this environment.'
  );
}

interface CreatePaymentIntentRequest {
  maxAmount: number;
  email: string;
  firstName: string;
  lastName: string;
  processingType: ProcessingType;
  s3Key: string;
  s3Bucket: string;
  companyName?: string;
  aiOverview?: boolean;
  savePaymentMethod?: boolean;
  paymentMethodId?: string; // NEW: For using saved payment methods
  address?: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  fileName?: string;
  totalRows?: number;
  processableRows?: number;
  jobId?: string;
  estimatedCost?: number;
}

interface CreatePaymentIntentResponse {
  success: boolean;
  client_secret: string;
  payment_intent_id: string;
  error?: string;
}

interface CapturePaymentRequest {
  paymentIntentId: string;
  actualAmount: number;
  successfulRows?: number;
  failedRows?: number;
}

interface CapturePaymentResponse {
  success: boolean;
  amount_captured: number;
  error?: string;
}

interface CancelPaymentRequest {
  paymentIntentId: string;
  reason?: string;
}

interface CancelPaymentResponse {
  success: boolean;
  error?: string;
}

interface CreateSingleSearchPaymentRequest {
  amount: number;
  email: string;
  firstName: string;
  lastName: string;
  searchType: SearchType;
  amountToCollect?: number; // Optional - only required for scoring operations
  description: string;
  companyName?: string;
  savePaymentMethod?: boolean;
  paymentMethodId?: string; // For using saved payment methods
}

export class StripeAPIService {
  private ensureApiUrl(action: string): string {
    if (!STRIPE_API_URL) {
      throw new ProcessingError(
        `Stripe API URL is not configured. Unable to ${action}. Please set VITE_STRIPE_API_URL.`
      );
    }
    return STRIPE_API_URL;
  }

  /**
   * Creates a Payment Intent with manual capture for pre-authorization
   */
  async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<CreatePaymentIntentResponse> {
    if (!data.maxAmount || data.maxAmount <= 0) {
      throw new ValidationError('Valid maximum amount is required', 'maxAmount');
    }

    if (!data.email || !data.email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    if (!data.processingType) {
      throw new ValidationError('Processing type is required', 'processingType');
    }

    if (!data.s3Key) {
      throw new ValidationError('S3 key is required', 's3Key');
    }

    if (!data.s3Bucket) {
      throw new ValidationError('S3 bucket is required', 's3Bucket');
    }

    logger.info('Creating Payment Intent for pre-authorization', {
      maxAmount: data.maxAmount,
      email: data.email,
      processingType: data.processingType,
      s3Key: data.s3Key,
      aiOverview: data.aiOverview ?? false,
      savePaymentMethod: data.savePaymentMethod ?? true,
    });

    try {
      const baseUrl = this.ensureApiUrl('create payment intents');
      const response = await fetch(`${baseUrl}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maxAmount: Math.round(data.maxAmount * 100), // Convert to cents
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          processingType: data.processingType,
          s3Key: data.s3Key,
          s3Bucket: data.s3Bucket,
          companyName: data.companyName,
          address: data.address,
          fileName: data.fileName,
          totalRows: data.totalRows,
          processableRows: data.processableRows,
          jobId: data.jobId,
          estimatedCost: data.estimatedCost,
          aiOverview: data.aiOverview ?? false,
          savePaymentMethod: data.savePaymentMethod ?? true,
          paymentMethodId: data.paymentMethodId, // Pass saved payment method ID if provided
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to create Payment Intent: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to create Payment Intent: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData: CreatePaymentIntentResponse = await response.json();
      
      if (!responseData.success) {
        logger.error('Stripe API returned failure for Payment Intent creation:', responseData.error);
        throw new ProcessingError(`Payment Intent creation failed: ${responseData.error || 'Unknown error'}`);
      }

      if (!responseData.client_secret || !responseData.payment_intent_id) {
        logger.error('Stripe API returned incomplete response:', responseData);
        throw new ProcessingError('Incomplete response from Payment Intent service');
      }

      logger.info('Payment Intent created successfully', {
        paymentIntentId: responseData.payment_intent_id
      });

      return responseData;

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      
      logger.error('Unexpected error creating Payment Intent:', error);
      throw new NetworkError(`Network error creating Payment Intent: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Captures the actual amount from a pre-authorized Payment Intent
   */
  async capturePayment(data: CapturePaymentRequest): Promise<CapturePaymentResponse> {
    if (!data.paymentIntentId) {
      throw new ValidationError('Payment Intent ID is required', 'paymentIntentId');
    }

    if (!data.actualAmount || data.actualAmount <= 0) {
      throw new ValidationError('Valid actual amount is required', 'actualAmount');
    }

    logger.info('Capturing payment for processed records', {
      paymentIntentId: data.paymentIntentId,
      actualAmount: data.actualAmount
    });

    try {
      const baseUrl = this.ensureApiUrl('capture payments');
      const response = await fetch(`${baseUrl}/capture-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: data.paymentIntentId,
          actualAmount: Math.round(data.actualAmount * 100), // Convert to cents
          successfulRows: data.successfulRows,
          failedRows: data.failedRows
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to capture payment: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to capture payment: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData: CapturePaymentResponse = await response.json();
      
      if (!responseData.success) {
        logger.error('Stripe API returned failure for payment capture:', responseData.error);
        throw new ProcessingError(`Payment capture failed: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Payment captured successfully', {
        paymentIntentId: data.paymentIntentId,
        amountCaptured: responseData.amount_captured / 100 // Convert back from cents
      });

      return responseData;

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      
      logger.error('Unexpected error capturing payment:', error);
      throw new NetworkError(`Network error capturing payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Cancels a pre-authorized Payment Intent if processing fails
   */
  async cancelPayment(data: CancelPaymentRequest): Promise<CancelPaymentResponse> {
    if (!data.paymentIntentId) {
      throw new ValidationError('Payment Intent ID is required', 'paymentIntentId');
    }

    logger.info('Canceling pre-authorized payment', {
      paymentIntentId: data.paymentIntentId
    });

    try {
      const baseUrl = this.ensureApiUrl('cancel payments');
      const response = await fetch(`${baseUrl}/cancel-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: data.paymentIntentId,
          reason: data.reason
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to cancel payment: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to cancel payment: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData: CancelPaymentResponse = await response.json();
      
      if (!responseData.success) {
        logger.error('Stripe API returned failure for payment cancellation:', responseData.error);
        throw new ProcessingError(`Payment cancellation failed: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Payment authorization cancelled successfully', {
        paymentIntentId: data.paymentIntentId
      });

      return responseData;

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      
      logger.error('Unexpected error cancelling payment:', error);
      throw new NetworkError(`Network error cancelling payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create a payment for single person search using immediate capture
   * Uses the existing /create-payment-intent endpoint with single-search specific metadata
   */
  async createSingleSearchPayment(data: CreateSingleSearchPaymentRequest): Promise<CreatePaymentIntentResponse> {
    if (!data.amount || data.amount <= 0) {
      throw new ValidationError('Valid amount is required', 'amount');
    }

    if (!data.firstName || !data.lastName) {
      throw new ValidationError('First name and last name are required', 'name');
    }

    if (!data.searchType) {
      throw new ValidationError('Search type is required', 'searchType');
    }

    logger.info('Creating single search payment', {
      amount: data.amount,
      email: data.email,
      searchType: data.searchType,
      firstName: data.firstName,
      lastName: data.lastName,
      savePaymentMethod: data.savePaymentMethod ?? true,
    });

    try {
      // Use the existing /create-payment-intent endpoint
      // For single search, we use manual capture with processableRows=1 to indicate single search
      const baseUrl = this.ensureApiUrl('create payment intents for single search');
      const response = await fetch(`${baseUrl}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maxAmount: Math.round(data.amount * 100), // Convert to cents
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          processingType: data.searchType, // 'scoring' or 'scoring_enrich'
          s3Key: 'single-search', // Placeholder for single search (not batch)
          s3Bucket: 'single-search', // Placeholder for single search (not batch)
          companyName: data.companyName || '', // Optional
          fileName: data.description,
          totalRows: 1, // Single search = 1 person
          processableRows: 1, // Single search = 1 person
          jobId: `single-search-${Date.now()}`,
          estimatedCost: data.amount,
          aiOverview: true, // Single searches always include AI overview
          savePaymentMethod: data.savePaymentMethod ?? true, // Save by default
          paymentMethodId: data.paymentMethodId, // Pass saved payment method ID if provided
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to create single search payment: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to create payment: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData: CreatePaymentIntentResponse = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for single search payment:', responseData.error);
        throw new ProcessingError(`Payment creation failed: ${responseData.error || 'Unknown error'}`);
      }

      if (!responseData.client_secret || !responseData.payment_intent_id) {
        logger.error('Stripe API returned incomplete response:', responseData);
        throw new ProcessingError('Incomplete response from payment service');
      }

      logger.info('Single search payment created successfully', {
        paymentIntentId: responseData.payment_intent_id
      });

      return responseData;

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error creating single search payment:', error);
      throw new NetworkError(`Network error creating payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * List payment methods for a customer by email
   */
  async listPaymentMethods(email: string): Promise<SavedPaymentMethod[]> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    logger.info('Fetching payment methods for email:', email);

    try {
      const baseUrl = this.ensureApiUrl('list payment methods');
      const response = await fetch(`${baseUrl}/list-payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to list payment methods: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to list payment methods: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for listing payment methods:', responseData.error);
        throw new ProcessingError(`Failed to list payment methods: ${responseData.error || 'Unknown error'}`);
      }

      logger.info(`Retrieved ${responseData.payment_methods?.length || 0} payment methods`);

      return responseData.payment_methods || [];

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error listing payment methods:', error);
      throw new NetworkError(`Network error listing payment methods: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Remove a payment method
   */
  async removePaymentMethod(email: string, paymentMethodId: string): Promise<void> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    if (!paymentMethodId) {
      throw new ValidationError('Payment method ID is required', 'paymentMethodId');
    }

    logger.info('Removing payment method:', { email, paymentMethodId });

    try {
      const baseUrl = this.ensureApiUrl('remove payment methods');
      const response = await fetch(`${baseUrl}/remove-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          paymentMethodId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to remove payment method: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to remove payment method: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for removing payment method:', responseData.error);
        throw new ProcessingError(`Failed to remove payment method: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Payment method removed successfully');

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error removing payment method:', error);
      throw new NetworkError(`Network error removing payment method: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(email: string, paymentMethodId: string): Promise<void> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    if (!paymentMethodId) {
      throw new ValidationError('Payment method ID is required', 'paymentMethodId');
    }

    logger.info('Setting default payment method:', { email, paymentMethodId });

    try {
      const baseUrl = this.ensureApiUrl('set default payment method');
      const response = await fetch(`${baseUrl}/set-default-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          paymentMethodId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to set default payment method: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to set default payment method: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for setting default payment method:', responseData.error);
        throw new ProcessingError(`Failed to set default payment method: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Default payment method updated successfully');

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error setting default payment method:', error);
      throw new NetworkError(`Network error setting default payment method: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get wallet balance for a customer
   */
  async getWalletBalance(email: string): Promise<number> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    logger.info('Fetching wallet balance for email:', email);

    try {
      const baseUrl = this.ensureApiUrl('get wallet balance');
      const response = await fetch(`${baseUrl}/wallet/balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to get wallet balance: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to get wallet balance: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for getting wallet balance:', responseData.error);
        throw new ProcessingError(`Failed to get wallet balance: ${responseData.error || 'Unknown error'}`);
      }

      logger.info(`Retrieved wallet balance: $${responseData.balance}`);

      return responseData.balance || 0;

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error getting wallet balance:', error);
      throw new NetworkError(`Network error getting wallet balance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Top up wallet balance
   */
  async walletTopUp(email: string, amount: number, paymentMethodId: string, firstName?: string, lastName?: string): Promise<{
    balance: number;
    topup_amount: number;
    bonus_amount: number;
    bonus_percent: number;
  }> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    if (!amount || amount < 5) {
      throw new ValidationError('Minimum wallet top-up is $5.00', 'amount');
    }

    if (!paymentMethodId) {
      throw new ValidationError('Payment method ID is required', 'paymentMethodId');
    }

    logger.info('Processing wallet top-up:', { email, amount });

    try {
      const baseUrl = this.ensureApiUrl('wallet top-up');
      const response = await fetch(`${baseUrl}/wallet/topup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          amount,
          paymentMethodId,
          firstName,
          lastName
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to process wallet top-up: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to process wallet top-up: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for wallet top-up:', responseData.error);
        throw new ProcessingError(`Wallet top-up failed: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Wallet top-up successful:', {
        balance: responseData.balance,
        topup_amount: responseData.topup_amount,
        bonus_amount: responseData.bonus_amount
      });

      return {
        balance: responseData.balance,
        topup_amount: responseData.topup_amount,
        bonus_amount: responseData.bonus_amount,
        bonus_percent: responseData.bonus_percent
      };

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error processing wallet top-up:', error);
      throw new NetworkError(`Network error processing wallet top-up: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get wallet transactions
   */
  async getWalletTransactions(email: string, limit: number = 50): Promise<any[]> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    logger.info('Fetching wallet transactions for email:', email);

    try {
      const baseUrl = this.ensureApiUrl('get wallet transactions');
      const response = await fetch(`${baseUrl}/wallet/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          limit
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to get wallet transactions: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to get wallet transactions: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for getting wallet transactions:', responseData.error);
        throw new ProcessingError(`Failed to get wallet transactions: ${responseData.error || 'Unknown error'}`);
      }

      logger.info(`Retrieved ${responseData.transactions?.length || 0} wallet transactions`);

      return responseData.transactions || [];

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error getting wallet transactions:', error);
      throw new NetworkError(`Network error getting wallet transactions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Deduct from wallet balance (for wallet-funded searches/batches)
   */
  async walletDeduct(
    email: string,
    amount: number,
    description: string,
    options?: {
      searchType?: string;
      processingType?: string;
      jobId?: string;
      searchId?: string;
    }
  ): Promise<{
    balance: number;
    deducted_amount: number;
    previous_balance: number;
  }> {
    if (!email || !email.includes('@')) {
      throw new ValidationError('Valid email address is required', 'email');
    }

    if (!amount || amount <= 0) {
      throw new ValidationError('Amount must be greater than 0', 'amount');
    }

    if (!description) {
      throw new ValidationError('Description is required', 'description');
    }

    logger.info('Processing wallet deduction:', { email, amount, description });

    try {
      const baseUrl = this.ensureApiUrl('wallet deduction');
      const response = await fetch(`${baseUrl}/wallet/deduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          amount,
          description,
          searchType: options?.searchType,
          processingType: options?.processingType,
          jobId: options?.jobId,
          searchId: options?.searchId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to process wallet deduction: ${response.status} ${response.statusText}`, errorText);
        throw new NetworkError(`Failed to process wallet deduction: ${response.status} ${response.statusText}`, response.status);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        logger.error('Stripe API returned failure for wallet deduction:', responseData.error);
        throw new ProcessingError(`Wallet deduction failed: ${responseData.error || 'Unknown error'}`);
      }

      logger.info('Wallet deduction successful:', {
        balance: responseData.balance,
        deducted_amount: responseData.deducted_amount
      });

      return {
        balance: responseData.balance,
        deducted_amount: responseData.deducted_amount,
        previous_balance: responseData.previous_balance
      };

    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }

      logger.error('Unexpected error processing wallet deduction:', error);
      throw new NetworkError(`Network error processing wallet deduction: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Export singleton instance
export const stripeAPI = new StripeAPIService();
